/**
 * How close a CRM plan's end date is — shared by the site-wide header banner
 * (shared/plan-expiry-banner) and the Settings → Plan tab, so the two cannot
 * disagree about when a plan counts as "expiring soon".
 */

/** Highlight the plan's expiry once this many days (or fewer) remain. */
export const PLAN_EXPIRY_WARNING_DAYS = 3;

export interface PlanExpiry {
  /** Negative once the end date has passed. 0 means the plan ends today. */
  readonly daysLeft: number;
  readonly isExpired: boolean;
  readonly isExpiringSoon: boolean;
}

/**
 * Days until `crm.clinic_subscriptions.end_date`, date-only with no time-of-day.
 * Null when the plan has no end date (e.g. a trial with no fixed term) — the
 * caller renders nothing rather than guessing.
 *
 * `end_date` is a plain SQL `date` carrying no timezone, so it is parsed as
 * LOCAL midnight and compared against a local midnight today. Do not "fix" this
 * to UTC: the clinic's own calendar day is what the date means, and shifting it
 * would flip the banner a day early or late for anyone east or west of UTC.
 */
export function planExpiryFromEndDate(endDate: string | null | undefined): PlanExpiry | null {
  if (!endDate) {
    return null;
  }

  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(end.getTime())) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysLeft = Math.round((end.getTime() - today.getTime()) / 86_400_000);

  return {
    daysLeft,
    isExpired: daysLeft < 0,
    isExpiringSoon: daysLeft >= 0 && daysLeft <= PLAN_EXPIRY_WARNING_DAYS,
  };
}
